// ============================================================================
// LoginPage — the real auth entry point (route "/login", namespace "login").
// ----------------------------------------------------------------------------
// A premium, mobile-first trainer sign-in that matches the bold Pulse landing
// aesthetic. Composition lives in app/login/*; this file orchestrates the split
// layout, the already-authed redirect, and the optional-first session wiring.
//
// • AUTH — unchanged. POSTs to /api/auth/login through useAuth().login (the
//   server sets the httponly vf_session cookie). On success we send the trainer
//   to wherever they were headed (or "/"). The status→message mapping (429 /
//   401 / generic) is preserved verbatim in <LoginForm/>. An already-authed
//   visitor is still bounced straight to their destination.
// • DESIGN — built entirely on the LOCKED Pulse design system (Card, Button,
//   TextField, Badge, StatTile, LanguageToggle + tokens). Nothing in the design
//   system is restyled; this page only composes it.
// • MOBILE-FIRST — designed at 390 first: a compact brand band leads, the form
//   sits immediately below; from lg up it becomes a two-up split (brand stage
//   left, form right) with each half a full-height column.
// • MOTION — Framer Motion throughout (staggered reveals, a looping brand
//   graphic, an error shake), all reduced-motion aware.
// • API / live-sync — wired through the typed API client + React Query via the
//   optional-first useLoginSession() probe, which shares qk.auth.me with the
//   auth layer & AppShell so it stays in lockstep. The page is 100% functional
//   with no session.
// ============================================================================
import { Navigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { LanguageToggle } from "../i18n/LanguageToggle";
import { LoginAside } from "./login/LoginAside";
import { LoginForm } from "./login/LoginForm";
import { useLoginSession } from "./login/useLoginSession";

interface FromState {
  from?: { pathname?: string };
}

function BrandMark() {
  const { t } = useTranslation();
  return (
    <Link to="/" className="group inline-flex items-center gap-2.5">
      <span className="grid h-9 w-9 place-items-center rounded-md bg-grad-coral shadow-volt">
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
          <path
            d="M3 13h4l2-6 3 11 2.6-8L18 13h3"
            stroke="#fff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="font-display text-lg font-extrabold tracking-tightest text-body">
        {t("app.name", { ns: "common" })}
      </span>
    </Link>
  );
}

export function LoginPage() {
  // Bind this page to its OWN i18n namespace (falls back to common.* for shared
  // keys like app.name and auth.* error strings).
  const { t, ready } = useTranslation("login");
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const session = useLoginSession();

  const dest = (location.state as FromState | null)?.from?.pathname ?? "/";

  // Already signed in? Skip the form entirely (preserved behaviour).
  if (!isLoading && isAuthenticated) return <Navigate to={dest} replace />;

  // Avoid a flash of untranslated keys before resources settle.
  if (!ready) return <div className="min-h-screen bg-paper" aria-hidden />;

  return (
    <div className="min-h-screen bg-paper text-body lg:grid lg:grid-cols-[1.05fr_0.95fr]">
      {/* Brand stage — a compact band on mobile, the full left column on lg. */}
      <LoginAside />

      {/* Form column. */}
      <div className="relative flex min-h-screen flex-col bg-surface lg:min-h-0">
        <header className="flex items-center justify-between px-gutter py-5 lg:py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 font-display text-sm font-bold text-body-soft transition-colors hover:text-body"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
              <path
                d="M15 6l-6 6 6 6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t("back")}
          </Link>
          <LanguageToggle />
        </header>

        <main className="flex flex-1 flex-col items-center justify-center px-gutter pb-12 pt-2 sm:pt-4">
          {/* Brand mark above the form for a confident, branded sign-in. */}
          <div className="mb-7 self-start sm:self-center lg:self-start lg:max-w-md lg:w-full">
            <BrandMark />
            <p className="mt-1 text-sm text-body-soft">{t("app.tagline", { ns: "common" })}</p>
          </div>
          <LoginForm session={session} dest={dest} />
        </main>
      </div>
    </div>
  );
}
